import MessagingCard from '@orn/shared-web/components/MessagingCard';
import Select, { type SelectItemType } from '@orn/shared-web/components/Select/Select';
import { AlertCircleIcon } from '@orn/shared-web/icons';
import logger from '@orn/shared-web/lib/logger';
import * as React from 'react';

import { VehicleOwnership } from '../../../__generated__/globalTypes';
import { useDeleteVehicle } from '../../../hooks/mutations/useDeleteVehicle';
import { useUpsertVehicle } from '../../../hooks/mutations/useUpsertVehicle';
import { useDependents } from '../../../hooks/queries/useDependents';
import { useVehicles } from '../../../hooks/queries/useVehicles';
import { VEHICLE_OWNERSHIP_OPTIONS } from '../../../lib/dependents/constants';
import { OnboardingTitle } from '../../OnboardingLayout';

import { getDependentPossessive } from './dependentUtils';
import OnboardingStepActions from './OnboardingStepActions';

import type { IVehicle } from '../../../hooks/queries/useVehicles';
import type { IOnboardingStepProps } from '../types';

const MAX_VEHICLE_COUNT = 5;

const VEHICLE_COUNT_OPTIONS: SelectItemType[] = Array.from({ length: MAX_VEHICLE_COUNT }, (_, index) => {
  const count = index + 1;
  return {
    id: String(count),
    label: count === 1 ? '1 vehicle' : `${count} vehicles`,
  };
});

const DEFAULT_OWNERSHIP_ID: VehicleOwnership = VehicleOwnership.OwnOutright;

/** Initial form state derived from any vehicles already saved on the family. */
const initialFormFromExisting = (existing: readonly IVehicle[]) => ({
  vehicleCountId: existing.length > 0 ? String(existing.length) : null,
  ownershipByIndex: Object.fromEntries(existing.map((v, i) => [i, v.ownership])) as Record<number, VehicleOwnership>,
});

/**
 * Onboarding vehicle-information screen — "How many vehicles?" plus a per-vehicle
 * ownership-type select rendered inside a stone-tinted card.
 *
 * On mount, fetches the family's existing vehicles via {@link useVehicles}
 * and pre-fills the count + ownership selects so a returning user sees their
 * prior answers. On Continue, walks the rows and upserts each (`useUpsertVehicle`
 * with `vehicleId` when an existing row is at that index, without it for new
 * rows); any trailing existing rows beyond the chosen count are removed via
 * `useDeleteVehicle` so the persisted list matches the form. "Skip for now"
 * advances without saving.
 *
 * The title names the dependent(s) ("Jane and Peter's transportation"), falling
 * back to "Your loved one's transportation" before the dependents query resolves.
 *
 * Figma: Mantle - UX/UI, node 4174:71747 "Onboarding Step 13".
 */
const DependentVehicleStep = ({ onNext, onBack }: IOnboardingStepProps) => {
  const { dependents } = useDependents();
  const { vehicles: existing, loading: existingLoading, refetch: refetchVehicles } = useVehicles();
  const [upsertVehicle] = useUpsertVehicle();
  const [deleteVehicle] = useDeleteVehicle();

  const [vehicleCountId, setVehicleCountId] = React.useState<string | null>(null);
  const [ownershipByIndex, setOwnershipByIndex] = React.useState<Record<number, VehicleOwnership>>({});
  const [hasInitialized, setHasInitialized] = React.useState(false);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [submitError, setSubmitError] = React.useState<string | null>(null);

  // Seed local state from the server once — subsequent refetches (e.g. from
  // our own upserts) must not stomp in-progress edits.
  React.useEffect(() => {
    if (hasInitialized || existingLoading) {
      return;
    }
    const initial = initialFormFromExisting(existing);
    setVehicleCountId(initial.vehicleCountId);
    setOwnershipByIndex(initial.ownershipByIndex);
    setHasInitialized(true);
  }, [existing, existingLoading, hasInitialized]);

  const vehicleCount = vehicleCountId ? Number(vehicleCountId) : 0;

  const setOwnership = React.useCallback((index: number, value: VehicleOwnership) => {
    setOwnershipByIndex((prev) => ({ ...prev, [index]: value }));
  }, []);

  const handleContinue = React.useCallback(async () => {
    setSubmitError(null);
    setIsSubmitting(true);
    try {
      // Upsert rows 0..vehicleCount-1: reuse the existing vehicleId at the
      // same index when available so partial form edits update in place.
      // Sequential (not Promise.all) so newly-created rows get monotonically
      // increasing `created_at` matching form order — the read path orders by
      // it (`VehicleController.getAllByFamilyId`), and parallel inserts would
      // land in non-deterministic order.
      for (let i = 0; i < vehicleCount; i++) {
        const existingVehicle = existing[i];
        const ownership = ownershipByIndex[i] ?? DEFAULT_OWNERSHIP_ID;
        await upsertVehicle({
          variables: {
            input: {
              vehicleId: existingVehicle?.id ?? null,
              ownership,
              ownershipOther: null,
              userId: null,
              dependantId: null,
              // This step only edits ownership; preserve make/model/year captured
              // elsewhere (e.g. the Possessions tab) since a null would clear them.
              year: existingVehicle?.year ?? null,
              make: existingVehicle?.make ?? null,
              model: existingVehicle?.model ?? null,
            },
          },
        });
      }
      // Delete trailing rows the user removed by reducing the count.
      const deletes = existing
        .slice(vehicleCount)
        .map((v) => deleteVehicle({ variables: { input: { vehicleId: v.id } } }));
      await Promise.all(deletes);
      await refetchVehicles();
      onNext();
    } catch (err) {
      logger.error('DependentVehicleStep save failed', err);
      setSubmitError(
        'Something went wrong saving your vehicles. Please try again — contact help@gomantle.com if the problem persists.'
      );
    } finally {
      setIsSubmitting(false);
    }
  }, [deleteVehicle, existing, onNext, ownershipByIndex, refetchVehicles, upsertVehicle, vehicleCount]);

  if (existingLoading && !hasInitialized) {
    return null;
  }

  const title = `${getDependentPossessive(dependents, "Your loved one's")} transportation`;

  return (
    <div className="flex w-full flex-col gap-6">
      <OnboardingTitle title={title} subtitle="Vehicle information helps us understand expenses." onBack={onBack} />

      <div className="flex w-full flex-col gap-6">
        <Select
          label="How many vehicles?"
          placeholder="Select an option"
          size="md"
          items={VEHICLE_COUNT_OPTIONS}
          selectedKey={vehicleCountId}
          onSelectionChange={(key) => setVehicleCountId(key === null ? null : String(key))}
        >
          {(item) => <Select.Item id={item.id}>{item.label}</Select.Item>}
        </Select>

        {vehicleCount > 0 && (
          <div className="bg-utility-stone-100 border-utility-stone-200 flex w-full flex-col gap-6 rounded-xl border p-6">
            {Array.from({ length: vehicleCount }, (_, index) => (
              <Select
                key={index}
                label={`Vehicle ${index + 1}`}
                size="md"
                items={VEHICLE_OWNERSHIP_OPTIONS}
                selectedKey={ownershipByIndex[index] ?? DEFAULT_OWNERSHIP_ID}
                onSelectionChange={(key) => setOwnership(index, key as VehicleOwnership)}
              >
                {(item) => <Select.Item id={item.id}>{item.label}</Select.Item>}
              </Select>
            ))}
          </div>
        )}

        {submitError && (
          <MessagingCard variant="error">
            <MessagingCard.Text icon={<AlertCircleIcon />}>{submitError}</MessagingCard.Text>
          </MessagingCard>
        )}
      </div>

      <OnboardingStepActions
        onContinue={() => void handleContinue()}
        isContinueLoading={isSubmitting}
        isContinueDisabled={vehicleCount === 0}
        onSkip={onNext}
        isSkipDisabled={isSubmitting}
      />
    </div>
  );
};

DependentVehicleStep.displayName = 'DependentVehicleStep';

export default DependentVehicleStep;
